import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import MythVsFact from '../components/MythVsFact'
import Quiz from '../components/Quiz'
import Timeline from '../components/Timeline'
import SEO from '../components/SEO'
import { AcademicCapIcon, ClockIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'

// Import content data
import mythsData from '../content/myths.json'
import quizzesData from '../content/quizzes.json'
import timelineData from '../content/timeline.json'

export default function Learn() {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('myths')

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['myths', 'timeline', 'quiz'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  const tabs = [
    { id: 'myths', label: 'Myths vs Facts', icon: QuestionMarkCircleIcon },
    { id: 'timeline', label: 'Timeline', icon: ClockIcon },
    { id: 'quiz', label: 'Knowledge Check', icon: AcademicCapIcon }
  ]

  return (
    <>
      <SEO 
        title="Learn"
        description="Explore the real history of systemic racism in America through interactive timelines, myth-busting facts, and knowledge checks. Challenge misconceptions and deepen your understanding."
        keywords="American history timeline, systemic racism facts, racial justice education, civil rights history, slavery history, Jim Crow, mass incarceration, redlining, educational resources"
        url="https://indoctrination365.com/learn"
      />
      <main className="py-16">
        <div id="top" />
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-6">Interactive Learning Hub</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore the real history, challenge misconceptions, and test your understanding 
            through interactive educational experiences.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-md font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 inline-flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-pressed={activeTab === tab.id}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {activeTab === 'myths' && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold mb-4">Myths vs Facts</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Challenge common misconceptions about American history and systemic racism. 
                  Click each card to reveal the truth behind the myth.
                </p>
              </div>
              <MythVsFact myths={mythsData} />
            </div>
          )}

          {activeTab === 'timeline' && (
            <Timeline events={timelineData} />
          )}

          {activeTab === 'quiz' && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold mb-4">Knowledge Check</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Test your understanding with questions designed to reinforce key concepts 
                  and encourage deeper thinking.
                </p>
              </div>
              <Quiz questions={quizzesData} />
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-primary-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-semibold mb-4">Ready to Learn More?</h2>
          <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
            These interactive experiences are just the beginning. Get the full book for 
            comprehensive understanding and practical tools for change.
          </p>
          <Link
            to="/book"
            className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 inline-block"
          >
            Get the Book
          </Link>
        </div>
      </div>
    </main>
    </>
  )
}